import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { filterAccountingOfficeUnits, getUserUnits, setCurrentUnit } from '@/modules/units'
import type { UserUnit } from '@/modules/units/types'
import { appConfig } from '@/lib/appConfig'
import { useUnitTranslation } from '@/shared/contexts/TranslationContext'
import { useUnit, useContextKey } from '@/modules/units/contexts/UnitContext'
import { ContextIcon } from '../../shared/components/ui/ContextIcon'

import type { UnitContext } from '@/modules/units/contexts/UnitContextTypes';

interface UnitSelectorProps {
  userId: string
  currentUnitId: string | null
  onUnitChange?: (unitId: string) => void
}

export function UnitSelector({ userId, currentUnitId, onUnitChange }: UnitSelectorProps) {
  const router = useNavigate()
  const { tu } = useUnitTranslation()
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const [units, setUnits] = useState<UserUnit[]>([])
  const [loading, setLoading] = useState(true)
  const [showDropdown, setShowDropdown] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({})

  // Pega o contexto atual (prioriza unidade selecionada)
  const appContext = useContextKey() as UnitContext
  const unitCtx = useUnit()
  
  // Use currentUnit from context instead of prop (more reactive)
  const effectiveCurrentUnitId = unitCtx.currentUnit?.id || currentUnitId

  const updateDropdownPosition = () => {
    const button = buttonRef.current
    if (!button) return

    const rect = button.getBoundingClientRect()
    // Align to the left of the button by default
    let left = rect.left
    
    // Check if it overflows the window width
    const dropdownWidth = 288 // w-72 = 18rem = 288px
    if (left + dropdownWidth > window.innerWidth) {
      // align to right if it overflows
      left = rect.right - dropdownWidth
    }
    
    // Ensure it doesn't go off-screen left
    if (left < 16) left = 16

    setDropdownStyle({
      position: 'fixed',
      top: rect.bottom + 8,
      left: left,
      width: dropdownWidth,
      zIndex: 9999, // Super high z-index
      maxHeight: `calc(100vh - ${rect.bottom + 20}px)`
    })
  }

  useEffect(() => {
    setMounted(true)
    loadUnits()
    
    // Listen for reload events
    const handleReloadUnits = () => {
      console.log('[UnitSelector] Reloading units...')
      loadUnits()
    }
    
    window.addEventListener('reloadUnits', handleReloadUnits)
    return () => window.removeEventListener('reloadUnits', handleReloadUnits)
  }, [userId])

  useEffect(() => {
    if (showDropdown) {
      updateDropdownPosition()
      window.addEventListener('resize', updateDropdownPosition)
      window.addEventListener('scroll', updateDropdownPosition, true)
    }
    return () => {
      window.removeEventListener('resize', updateDropdownPosition)
      window.removeEventListener('scroll', updateDropdownPosition, true)
    }
  }, [showDropdown])

  const loadUnits = async () => {
    try {
      let userUnits = await getUserUnits(userId)
      if (appConfig.fixedContext) {
        userUnits = userUnits.filter(u => u.context_key === appConfig.fixedContext)
      }
      if (appContext === 'accounting') {
        userUnits = await filterAccountingOfficeUnits(userUnits)
      }
      setUnits(userUnits)
    } catch (error) {
      console.error('Error loading units:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleUnitChange = async (unitId: string) => {
    try {
      console.log('[UnitSelector] Changing unit to:', unitId)
      
      // Prefer using UnitProvider's setter so UI updates without full reload
      if (unitCtx && unitCtx.setCurrentUnit) {
        await unitCtx.setCurrentUnit(unitId)
        console.log('[UnitSelector] Unit changed via UnitProvider')
      } else {
        await setCurrentUnit(userId, unitId)
        console.log('[UnitSelector] Unit changed via direct call')
      }

      setShowDropdown(false)
      if (onUnitChange) onUnitChange(unitId)
    } catch (error) {
      console.error('[UnitSelector] Error changing unit:', error)
    }
  }

  const currentUnit = units.find(u => u.id === effectiveCurrentUnitId)
  
  console.log('[UnitSelector] Render:', {
    effectiveCurrentUnitId,
    currentUnitFromContext: unitCtx.currentUnit?.id,
    currentUnitFromProp: currentUnitId,
    currentUnitName: currentUnit?.name,
    unitsCount: units.length
  })

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-emerald-600" />
        <span className="hidden sm:inline">Carregando...</span>
      </div>
    )
  }

  if (units.length === 0) {
    // Se há contexto fixo, mostra botão para ir à lista de unidades
    if (appConfig.fixedContext) {
      return (
        <button
          onClick={() => router('/units')}
          className="flex items-center gap-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 px-3 py-2 text-sm font-medium text-white transition-colors"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          <span className="hidden sm:inline">{tu('createNew')}</span>
        </button>
      )
    }
    return (
      <button
        onClick={() => router('/onboarding')}
        className="flex items-center gap-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 px-3 py-2 text-sm font-medium text-white transition-colors"
      >
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
        <span className="hidden sm:inline">{tu('createNew')}</span>
      </button>
    )
  }

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => setShowDropdown(!showDropdown)}
        className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
      >
        <ContextIcon context={appContext} className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
        <span className="text-sm font-semibold text-slate-900 dark:text-white truncate max-w-[150px] sm:max-w-[200px]">
          {currentUnit?.name || tu('select')}
        </span>
        <svg className="h-4 w-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {showDropdown && mounted && createPortal(
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-[9990]" 
            onClick={() => setShowDropdown(false)}
          />
          
          {/* Dropdown */}
          <div 
            style={dropdownStyle}
            className="rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg overflow-y-auto"
          >
            <div className="px-4 py-2 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider border-b border-slate-200 dark:border-slate-700">
              {tu('list.title')} ({units.length})
            </div>
            
            {units.map(unit => (
              <button
                key={unit.id}
                onClick={() => handleUnitChange(unit.id)}
                className={`flex w-full items-center justify-between px-4 py-3 text-sm hover:bg-slate-100 dark:hover:bg-slate-700 ${
                  unit.id === effectiveCurrentUnitId
                    ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300'
                    : 'text-slate-700 dark:text-slate-300'
                }`}
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <div className="text-left flex-1 min-w-0">
                    <p className="font-medium truncate">{unit.name}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 capitalize">
                      {unit.role === 'admin' ? tu('list.admin') : 
                       unit.role === 'manager' ? tu('list.manager') : tu('list.viewer')}
                    </p>
                  </div>
                </div>
                {unit.id === effectiveCurrentUnitId && (
                  <svg className="h-5 w-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            ))}
            
            {/* Manage Units Link */}
            <div className="border-t border-slate-200 dark:border-slate-700 mt-2 pt-2">
              <button
                onClick={() => {
                  setShowDropdown(false)
                  router('/units')
                }}
                className="flex w-full items-center gap-3 px-4 py-2 text-sm text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 font-medium"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                {tu('list.title')}
              </button>
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  )
}
